import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Zap, Target, Users } from 'lucide-react';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

// Composant pour les étoiles subtiles
const FloatingStars = ({ prefersReducedMotion }) => {
  const stars = Array.from({ length: 120 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    delay: Math.random() * 8,
    duration: 4 + Math.random() * 6,
    size: 0.5 + Math.random() * 2.5,
    moveX: (Math.random() - 0.5) * 50,
    moveY: (Math.random() - 0.5) * 50,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute rounded-full"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: star.size,
            height: star.size,
            backgroundColor: '#ffffff',
            boxShadow: '0 0 4px rgba(255, 255, 255, 0.6)',
          }}
          animate={prefersReducedMotion ? {} : {
            opacity: [0.1, 0.8, 0.1],
            scale: [0.6, 1.4, 0.6],
            x: [0, star.moveX, 0],
            y: [0, star.moveY, 0],
          }}
          transition={{
            duration: star.duration,
            delay: star.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

export const HeroSectionHTAG = ({ onContactClick }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 50]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Apple-style gradient background */}
      <div className="absolute inset-0 apple-hero-bg" />
      
      {/* Floating stars - subtle dimension effect */}
      <FloatingStars prefersReducedMotion={prefersReducedMotion} />
      
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={prefersReducedMotion ? {} : {
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(0, 113, 227, 0.4) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <motion.div
          animate={prefersReducedMotion ? {} : {
            x: [0, -100, 0],
            y: [0, 100, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
      </div>

      {/* Main content with parallax */}
      <motion.div
        style={prefersReducedMotion ? {} : { opacity, scale, y }}
        className="relative z-10 container mx-auto text-center"
      >
        {/* Toggle Professionnels / Particuliers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mb-12 flex justify-center gap-2"
        >
          <button
            className="px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, #0071e3 0%, #005bb5 100%)',
              color: '#ffffff',
              boxShadow: '0 4px 12px rgba(0, 113, 227, 0.3)'
            }}
          >
            Professionnels
          </button>
          <button
            onClick={() => navigate('/particuliers')}
            className="px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 hover:bg-white/10"
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#f5f5f7'
            }}
          >
            Particuliers
          </button>
        </motion.div>

        {/* Main title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="apple-title mb-6"
        >
          Solutions numériques
          <br />
          <span className="apple-gradient-text">
            pour les professionnels
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="apple-subtitle max-w-3xl mx-auto mb-12"
        >
          IA, automatisations, design web, assistance et coaching.
          <br />
          Pour PME, associations et indépendants.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <button
            className="apple-button px-8 py-3 text-lg font-semibold rounded-full shadow-lg bg-gradient-to-r from-blue-600 to-blue-800 text-white"
            onClick={onContactClick}
          >
            Discutons de votre projet
          </button>
          <button
            className="apple-button px-8 py-3 text-lg font-semibold rounded-full shadow-lg bg-white/10 border border-white/20 text-white"
            onClick={() => {
              const section = document.getElementById('services');
              if (section) {
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } else {
                navigate('/#services');
              }
            }}
          >
            Découvrez nos services
          </button>
        </motion.div>
      </motion.div>

      {/* Feature highlights */}
      <motion.div
        style={prefersReducedMotion ? {} : { opacity, scale, y }}
        className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
      >
        {[
          { Icon: Zap, text: 'Réactivité immédiate' },
          { Icon: Target, text: 'Solutions sur mesure' },
          { Icon: Users, text: 'Accompagnement personnalisé' }
        ].map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 + index * 0.1 }}
            className="flex flex-col items-center gap-2"
          >
            <item.Icon className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
            <span className="text-sm text-gray-400">{item.text}</span>
          </motion.div>
        ))}
      </motion.div>
    

      {/* Scroll indicator */}
      <motion.div
        animate={prefersReducedMotion ? {} : { y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden md:flex flex-col items-center pointer-events-none"
      >
        <div className="text-center">
          <p className="text-gray-500 text-sm mb-2">Découvrez nos services</p>
          <div className="w-6 h-10 border-2 border-gray-600 rounded-full flex justify-center mx-auto">
            <motion.div
              animate={prefersReducedMotion ? {} : { y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-2 bg-gray-400 rounded-full mt-2"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};
